// src/cockpit/CockpitPage.tsx
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppStore } from '../shell/appStore'
import { useCockpit, useSetManualStat } from './useCockpit'
import { STAGE_LABELS } from '../crm/pipeline/stages'
import { CONTENT_STATUS_LABELS } from '../marketing/content/statuses'
import { ugcActionCounts, type UgcOverview } from '../lib/ugcTypes'

const GATE_METRIC_LABELS: Record<string, string> = {
  trial_conversion_rate: 'Trial → payé',
  d30_retention: 'Rétention D30',
  crash_free: 'Sessions sans crash',
  reviews_count: "Nombre d'avis",
  reviews_avg: 'Note moyenne',
}

function formatGateValue(metric: string, value: number): string {
  if (metric === 'reviews_count') return String(Math.round(value))
  if (metric === 'reviews_avg') return value.toFixed(2)
  // Métrique hors gate (ex. cible mrr posée dans stats_targets) : valeur brute —
  // le format « fraction → % » n'a de sens que pour les taux du gate paid.
  if (!(metric in GATE_METRIC_LABELS)) return String(value)
  return `${(value * 100).toFixed(1)} %`
}

const SHORTCUTS: Array<[string, string]> = [
  ['Rapport du jour', '/reports'],
  ['Saisir une dépense', '/marketing/channels'],
  ['Nouveau contenu', '/marketing/content/new'],
  ['Nouveau contact', '/crm/contacts/new'],
  ['Tâches', '/crm/tasks'],
]

const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: '10px',
  padding: '1.25rem',
}

const sectionTitle: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--dim)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  margin: '0 0 0.75rem',
}

export function CockpitPage() {
  const { activeAppId } = useAppStore()
  const { data, isLoading, isError } = useCockpit(activeAppId)
  const setManual = useSetManualStat(activeAppId)
  const [manualDrafts, setManualDrafts] = useState<Record<string, string>>({})

  if (!activeAppId) {
    return (
      <div style={{ padding: '2rem 2.5rem' }}>
        <p style={{ color: 'var(--muted)' }}>Sélectionnez une app pour voir le cockpit.</p>
      </div>
    )
  }

  if (isLoading || !data) {
    return <div style={{ padding: '2rem 2.5rem' }}><p style={{ color: 'var(--muted)' }}>{isError ? 'Erreur de chargement du cockpit.' : 'Chargement…'}</p></div>
  }

  const ugcRows = ugcActionRows(data.ugc)
  const nbActions = data.tasks.length + data.relances.length + data.contents.length + data.jalons.length + ugcRows.length

  return (
    <div style={{ padding: '2rem 2.5rem', maxWidth: '1100px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--fg)', margin: 0, letterSpacing: '-0.025em' }}>
          Cockpit
        </h1>
        {/* Raccourcis */}
        <div data-testid="cockpit-shortcuts" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {SHORTCUTS.map(([label, to]) => (
            <Link key={to} to={to} style={{ background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '7px', padding: '0.375rem 0.75rem', color: 'var(--muted)', fontSize: '0.8125rem', textDecoration: 'none' }}>
              {label}
            </Link>
          ))}
        </div>
      </div>

      {/* À faire aujourd'hui */}
      <section data-testid="cockpit-actions" style={card}>
        <h2 style={sectionTitle}>À faire aujourd'hui{nbActions > 0 ? ` (${nbActions})` : ''}</h2>
        {nbActions === 0 && <p style={{ color: 'var(--muted)', fontSize: '0.875rem', margin: 0 }}>Rien d'urgent — tout est à jour.</p>}
        {data.tasks.map((t) => (
          <ActionRow key={`task-${t.id}`} tag="Tâche" tagColor="var(--arancio)" to="/crm/tasks"
            text={t.title} meta={t.dueDate ?? ''} />
        ))}
        {data.relances.map((r) => (
          <ActionRow key={`rel-${r.dealId}`} tag="Relance" tagColor="#e8c46a" to={`/crm/contacts/${r.creatorId}`}
            text={`${r.creatorName} — ${STAGE_LABELS[r.stage]}`} meta={`sans réponse depuis le ${r.updatedAt.slice(0, 10)}`} />
        ))}
        {data.contents.map((c) => (
          <ActionRow key={`cnt-${c.id}`} tag="Contenu" tagColor="#5FA8D3" to={`/marketing/content/${c.id}`}
            text={c.title} meta={`${CONTENT_STATUS_LABELS[c.status]} · prévu ${c.plannedDate ?? '—'}`} />
        ))}
        {data.jalons.map((j) => (
          <ActionRow key={`jal-${j.id}`} tag="Jalon" tagColor="#7FBF7F" to="/marketing/launch"
            text={j.title} meta={`${j.milestone} · ${j.date}`} />
        ))}
        {ugcRows.map((r) => (
          <ActionRow key={`ugc-${r.to}`} tag="UGC" tagColor="var(--bronze)" to={r.to} text={r.text} meta="Programme UGC" />
        ))}
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', alignItems: 'start' }}>
        {/* Budget */}
        <section data-testid="cockpit-budget" style={card}>
          <h2 style={sectionTitle}>Budget</h2>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <Kpi label="Aujourd'hui" value={`${data.budget.today.toFixed(2)} €`} />
            <Kpi label="7 jours" value={`${data.budget.last7.toFixed(2)} €`} />
            <Kpi label="Mois en cours" value={`${data.budget.monthToDate.toFixed(2)} €`} sub={`prévu : ${data.budget.plannedTotal.toFixed(2)} €`} />
          </div>
        </section>

        {/* Gate paid */}
        <section data-testid="cockpit-gate" style={{ ...card, borderColor: data.gate.allMet ? 'rgba(76,175,125,0.5)' : 'var(--line)' }}>
          <h2 style={sectionTitle}>
            Gate paid {data.gate.configured ? (data.gate.allMet ? '· ✅ ouvert' : '· fermé') : ''}
          </h2>
          {!data.gate.configured ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem', margin: 0 }}>
              Non configuré — seedez les critères (scripts/seed-gate-basalt.mjs).
            </p>
          ) : (
            data.gate.criteria.map((c, i) => (
              // key composite : stats_targets n'a pas d'unicité (app, metric) —
              // le seed E2E porte deux cibles mrr, une key `c.metric` seule dupliquerait.
              <div key={`${c.metric}-${i}`} data-testid={`gate-${c.metric}`} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.375rem 0', borderBottom: '1px solid var(--line)', fontSize: '0.8125rem' }}>
                <span style={{ width: '10px', color: c.met ? '#4CAF7D' : 'var(--arancio)' }}>{c.met ? '●' : '○'}</span>
                <span style={{ flex: 1, color: 'var(--fg)' }}>{GATE_METRIC_LABELS[c.metric] ?? c.metric}</span>
                <span style={{ color: 'var(--muted)' }}>
                  {c.current !== null ? formatGateValue(c.metric, c.current) : '—'} / {formatGateValue(c.metric, c.target)}
                </span>
                {c.manual && (
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.001}
                    aria-label={`Saisir ${GATE_METRIC_LABELS[c.metric] ?? c.metric}`}
                    placeholder="0.00"
                    value={manualDrafts[c.metric] ?? ''}
                    onChange={(e) => setManualDrafts((d) => ({ ...d, [c.metric]: e.target.value }))}
                    onBlur={() => {
                      const v = parseFloat(manualDrafts[c.metric] ?? '')
                      if (Number.isFinite(v)) {
                        setManual.mutate({ metric: c.metric as 'd30_retention' | 'crash_free', value: v })
                      }
                    }}
                    style={{ background: 'var(--elevated)', border: '1px solid var(--line-strong)', borderRadius: '6px', padding: '0.1875rem 0.375rem', color: 'var(--fg)', fontSize: '0.75rem', width: '72px' }}
                  />
                )}
              </div>
            ))
          )}
          {setManual.isError && (
            <p role="alert" style={{ margin: '0.625rem 0 0', color: 'var(--arancio)', fontSize: '0.8125rem' }}>
              Saisie refusée : {setManual.error.message} (fraction attendue entre 0 et 1, ex. 0.09).
            </p>
          )}
        </section>
      </div>
    </div>
  )
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`
}

/** Lignes « Programme UGC » de « À faire aujourd'hui » (spec § 6.7), une par onglet qui attend une action. */
function ugcActionRows(ugc: UgcOverview | null): { to: string; text: string }[] {
  if (!ugc) return []
  const counts = ugcActionCounts(ugc)
  const rows: { to: string; text: string }[] = []
  if (counts.applications > 0) {
    rows.push({ to: '/crm/ugc/candidatures', text: `${plural(counts.applications, 'candidature', 'candidatures')} à traiter` })
  }
  if (counts.videos > 0) {
    // Comme l'e-mail du matin : en retard (idées et brouillons), puis idées proches du délai sans retard.
    const late = ugc.ideasLate + ugc.draftsLate
    const nearOnly = Math.max(0, ugc.ideasNearDeadline - ugc.ideasLate)
    const parts = [
      ...(late > 0 ? [`${late} en retard`] : []),
      ...(nearOnly > 0 ? [`${plural(nearOnly, 'idée proche', 'idées proches')} des 48 h`] : []),
    ]
    const near = parts.length > 0 ? `, dont ${parts.join(' et ')}` : ''
    rows.push({ to: '/crm/ugc/videos', text: `${plural(counts.videos, 'vidéo', 'vidéos')} à relire${near}` })
  }
  if (counts.payments > 0) {
    rows.push({
      to: '/crm/ugc/paiements',
      text: `${plural(counts.payments, 'capture ou règlement', 'captures ou règlements')} à faire`,
    })
  }
  if (counts.members > 0) {
    const toActivate = ugc.proRequests > 0 ? `${ugc.proRequests} Pro à activer` : ''
    const toRemove = ugc.proRemovals > 0 ? `${ugc.proRemovals}${toActivate ? '' : ' Pro'} à retirer` : ''
    rows.push({ to: '/crm/ugc/pro', text: [toActivate, toRemove].filter(Boolean).join(', ') })
  }
  if (counts.messages > 0) {
    rows.push({
      to: '/crm/ugc/messages',
      text: `${plural(counts.messages, 'message non lu', 'messages non lus')} dans le chat`,
    })
  }
  return rows
}

function ActionRow({ tag, tagColor, to, text, meta }: {
  tag: string; tagColor: string; to: string; text: string; meta: string
}) {
  return (
    <Link to={to} style={{ display: 'flex', alignItems: 'baseline', gap: '0.625rem', padding: '0.375rem 0', borderBottom: '1px solid var(--line)', textDecoration: 'none' }}>
      <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: tagColor, textTransform: 'uppercase', letterSpacing: '.06em', width: '64px', flexShrink: 0 }}>{tag}</span>
      <span style={{ color: 'var(--fg)', fontSize: '0.875rem', flex: 1 }}>{text}</span>
      <span style={{ color: 'var(--dim)', fontSize: '0.75rem' }}>{meta}</span>
    </Link>
  )
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--muted)' }}>{label}</p>
      <p style={{ margin: '0.125rem 0 0', fontSize: '1.125rem', fontWeight: 700, color: 'var(--fg)' }}>{value}</p>
      {sub && <p style={{ margin: '0.125rem 0 0', fontSize: '0.75rem', color: 'var(--dim)' }}>{sub}</p>}
    </div>
  )
}
